import {
  ArcElement,
  type Chart,
  type ChartData,
  Chart as ChartJS,
  type ChartTypeRegistry,
  Legend,
  type LegendItem,
  Title,
  Tooltip,
} from 'chart.js';
import { useId, useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { accountingFormatNumber, getLastSevenDays } from '../../../../utils';

ChartJS.register(ArcElement, Title, Tooltip, Legend);

export default function DoughnutChart({
  dataLabels,
  datasets,
  display = true,
  percentage = false,
  showValueAndPercentage,
  fontSize = 16,
  padding = 20,
  legendPosition,
  legendAlign,
  cutout,
}: {
  dataLabels: string[];
  colors?: boolean;
  datasets: { data: number; formattedData?: string; color: string }[];
  display?: boolean;
  percentage?: boolean;
  showValueAndPercentage?: boolean;
  fontSize?: number;
  padding?: number;
  legendPosition?: any;
  legendAlign?: any;
  cutout?: string;
}) {
  const chartId = useId();
  const lastSevendaysFormatted: string[] = [];
  for (const date of getLastSevenDays()) {
    lastSevendaysFormatted.push(date.toDateString());
  }

  const _barCount = Math.floor(Math.random() * (10 - 5) + 5);
  const randomData = [];
  for (let i = 0; i < dataLabels?.length; i++) {
    randomData.push(Math.floor(Math.random() * (100 - 1) + 1));
  }

  const userData: any = [];
  const dataColors: string[] = [];
  if (datasets) {
    for (const d of datasets) {
      userData.push(d.data);
      dataColors.push(d.color);
    }
  }

  const getLabelText = (data: ChartData<keyof ChartTypeRegistry, (number | null)[], unknown>, label: string, i: number) => {
    if (showValueAndPercentage) {
      return `${label}: ${datasets[i].formattedData}`;
    }

    if (percentage) {
      return `${label}: ${data?.datasets[0]?.data[i]}%}`;
    }

    return `${label}: ${accountingFormatNumber({
      num: data?.datasets[0]?.data[i] ?? 0,
      format: 'en-US',
      currency: 'USD',
    })}`;
  };

  const chartKey = useMemo(() => JSON.stringify({ labels: dataLabels, datasets }), [dataLabels, datasets]);

  return (
    <Doughnut
      key={chartKey}
      className={showValueAndPercentage ? 'p-0' : 'p-8'}
      id={chartId}
      data={{
        labels: dataLabels,
        datasets: [
          {
            label: '',
            data: userData,
            backgroundColor: dataColors,
          },
        ],
      }}
      options={{
        maintainAspectRatio: true,
        cutout,
        plugins: {
          legend: {
            position: legendPosition ? legendPosition : 'bottom',
            align: legendAlign ? legendAlign : 'start',
            display: display,
            labels: {
              usePointStyle: true,
              font: { size: fontSize },
              pointStyle: 'circle',
              padding,
              generateLabels: (chart: Chart<keyof ChartTypeRegistry, (number | null)[], unknown>): LegendItem[] => {
                const { data } = chart;

                if (data?.labels?.length) {
                  return data.labels.map((label, i) => ({
                    text: getLabelText(data, label as string, i),
                    fillStyle: dataColors[i],
                    strokeStyle: dataColors[i],
                    lineWidth: 2,
                    datasetIndex: 0,
                  }));
                }
                return [];
              },
            },
          },
        },
      }}
    />
  );
}
